/* shark proxy telemetry */

export enum ETelemetryGroupBy {
  TENAT = 'GROUP_BY_TENANT',
  API_KEY = 'GROUP_BY_API_KEY',
  PROTOCOL = 'GROUP_BY_PROTOCOL',
  BLOCKCHAIN = 'GROUP_BY_BLOCKCHAIN',
  METHOD = 'GROUP_BY_METHOD',
}

export enum ETelemetryTopOf {
  API_KEY = 'TOP_OF_API_KEY_MASK',
  API_KEY_BY_TRAFFIC = 'TOP_OF_API_KEY_BY_TRAFFIC',
  BLOCKCHAIN = 'TOP_OF_BLOCKCHAIN',
  BLOCKCHAIN_BY_TRAFFIC = 'TOP_OF_BLOCKCHAIN_BY_TRAFFIC',
  COUNTRY = 'TOP_OF_COUNTRY',
  COUNTRY_BY_TRAFFIC = 'TOP_OF_COUNTRY_BY_TRAFFIC',
  ERROR = 'TOP_OF_ERROR',
  IP = 'TOP_OF_IP',
  IP_BY_TRAFFIC = 'TOP_OF_IP_BY_TRAFFIC',
  METHOD = 'TOP_OF_METHOD',
  METHOD_BY_TRAFFIC = 'TOP_OF_METHOD_BY_TRAFFIC',
  MONTHLY_USAGE = 'TOP_OF_MONTHLY_USAGE',
  MONTHLY_USAGE_BY_TRAFFIC = 'TOP_OF_MONTHLY_USAGE_BY_TRAFFIC',
  PROTOCOL = 'TOP_OF_PROTOCOL',
}

export interface IUsageTopElement {
  name: string;
  count: number | string;
}

export interface IUsageTop {
  top_of: ETelemetryTopOf;
  elements: IUsageTopElement[] | null;
}

export interface IUsageDataMRPCRequestBodyParams {
  api_key_values?: string[]; // List of user tokens to filter the data by
  blockchains?: string[]; // List of blockchains in MRPC format to include in the query
  end: string; // RFC 3339 timestamp
  group_by?: ETelemetryGroupBy; // Specifies how to group the results
  include_top_ofs?: ETelemetryTopOf[]; // Specific metrics to include in the results
  include_traffic_timeline?: boolean;
  interval: string; // Interval in seconds between data points, e.g., "86400s" for 1 day
  start: string; // RFC 3339 timestamp
  top_limit?: number; // Number of top items to return, default is 10
}

export interface IUsageDataMRPCRequestQueryParams {
  unfiltered?: boolean; // If true, the results will not be filtered by the user token
}

export interface IUsageDataMRPCRequestParams
  extends IUsageDataMRPCRequestBodyParams,
    IUsageDataMRPCRequestQueryParams {}

export interface RequestTimelinePoint {
  time: string; // RFC 3339 timestamp ("1710460800")
  value: number; // Value at the given time ("22282")
}

interface RequestTimeline {
  name: string; // Name of the user token
  points: RequestTimelinePoint[]; // List of data points
}

export interface IUsageDataMRPCResponse {
  all_time_total: string | number; // All-time total number of requests
  request_timelines: RequestTimeline[]; // List of request timelines
  tops: IUsageTop[]; // List of top metrics
  total: number | string; // Total number of requests
  total_bytes: string;
  traffic_timelines?: RequestTimeline[];
}

/* Raw requests statistics */

export interface ICustomerRequestsMRPCRequestBodyParams {
  api_key_values?: string[]; // Subset of owned api keys to filter by; empty means "all keys"
  blockchains?: string[]; // List of blockchains in MRPC format
  countries?: string[]; // Country codes filter
  end?: string; // RFC 3339 timestamp
  error_codes?: number[]; // Filter by specific error codes
  error_messages?: string[]; // Free-text substrings; matches when error_message contains any of these
  is_batch_filter?: number; // 0=any, 1=batch only, 2=non-batch only
  is_error?: boolean; // Filter only error/non-error requests
  methods?: string[]; // RPC methods filter
  min_duration_ms?: number; // Minimum request duration in ms
  page_size?: number; // Page size
  page_token?: string; // Pagination cursor
  protocols?: string[]; // Protocols filter
  start?: string; // RFC 3339 timestamp
  trace_id?: string; // Single-trace lookup
}

export interface ICustomerRequestsMRPCRequestQueryParams {
  unfiltered?: boolean;
}

export interface ICustomerRequestsMRPCRequestParams
  extends ICustomerRequestsMRPCRequestBodyParams,
    ICustomerRequestsMRPCRequestQueryParams {}

export enum ECustomerRequestIpVersion {
  IPv4 = 'IPv4',
  IPv6 = 'IPv6',
}

export enum ECustomerRequestMessageType {
  CALL = 'MESSAGE_TYPE_CALL',
  SUBSCRIBE = 'MESSAGE_TYPE_SUBSCRIBE',
  UNSUBSCRIBE = 'MESSAGE_TYPE_UNSUBSCRIBE',
}

export interface ICustomerRequestTimestamp {
  seconds: number; // Unix timestamp in seconds
  nanos?: number;
}

export interface ICustomerRequestCall {
  method?: string;
  status?: number;
  error_code?: number;
  error_message?: string;
  is_streamed?: boolean;
  is_archive?: boolean;
  retries?: number;
  block_tag?: string;
  detected_block_height?: number;
  detected_block_height_type?: number;
  message_type?: ECustomerRequestMessageType | string;
}

export interface ICustomerRequest {
  trace_id?: string;
  timestamp: ICustomerRequestTimestamp;
  api_key_masked: string;
  blockchain: string;
  protocol: string;
  is_batch?: boolean;
  duration_ms?: number;
  request_bytes: number;
  response_bytes: number;
  status?: number;
  error_code?: number;
  error_messages?: string[];
  ip_version: ECustomerRequestIpVersion | string;
  ip_network: string; // Truncated to /24 (v4) or /48 (v6) by upstream
  continent?: string;
  country?: string;
  city?: string;
  user_agent?: string;
  calls: ICustomerRequestCall[];
  host?: string;
}

export type ICustomerRequestsMRPCResponse = {
  data: ICustomerRequest[];
  next_page_token?: string;
};
